// src/components/media/MediaGrid.tsx — one folder's files as a grid of thumbnails, with everything staff do to them
// (docs/media-library.md): upload (drag files in from the desktop, or pick them; the label step first), drag to
// reorder (the first photo is the cover), select (click, shift-click, ⌘/Ctrl-click, select all), copy / cut / paste
// (also ⌘/Ctrl C X V), move (or drag the selection onto a folder in the tree), download one or a zip, rename, delete
// (our own confirm), and a preview with the label and caption editing. Used by Marketing › Media and by the Images
// tabs on a building and a flat. Buttons follow media.create / update / delete; the API checks them again.
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DndContext, DragOverlay, KeyboardSensor, PointerSensor, closestCenter, pointerWithin, useDroppable, useSensor, useSensors,
  type CollisionDetection, type DragEndEvent, type DragStartEvent,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, rectSortingStrategy, sortableKeyboardCoordinates, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Check, Star, UploadCloud } from 'lucide-react';
import { Button, EmptyState, Input, Sheet, Skeleton } from '../ui';
import { Confirm } from '../forms/common';
import { useCan } from '../../hooks/usePermission';
import { FolderTree, FOLDER_ICON } from './FolderTree';
import { MediaThumb } from './MediaThumb';
import { MediaPreview } from './MediaPreview';
import { UploadSheet } from './UploadSheet';
import { useToast } from './toast';
import {
  COVER_FOLDERS, MEDIA_ACCEPT, downloadItem, downloadZip, mediaApi, useMediaClipboard,
  type FolderKind, type MediaFolder, type MediaItem,
} from '../../lib/media';

export interface GridFolder { id: string; name: string; kind: FolderKind; isAuto: boolean; archived?: boolean }

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
/** A folder drop target (a tree node or a sub-folder tile) → its folder id. */
const dropFolder = (id: unknown) => /^(?:folder|tile):(.+)$/.exec(String(id))?.[1] ?? null;
const isTyping = (t: EventTarget | null) => {
  const el = t as HTMLElement | null;
  return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable);
};

/** Folder drop targets win when the pointer is on one; otherwise the nearest tile (reorder). */
const collide: CollisionDetection = (args) => {
  const within = pointerWithin(args);
  const folder = within.find((c) => dropFolder(c.id));
  if (folder) return [folder];
  return closestCenter({ ...args, droppableContainers: args.droppableContainers.filter((c) => !dropFolder(c.id)) });
};

function Tile({ item, selected, cut, cover, canDrag, onPointer, onOpen, onCheck }: {
  item: MediaItem; selected: boolean; cut: boolean; cover: boolean; canDrag: boolean;
  onPointer: (e: React.MouseEvent) => void; onOpen: () => void; onCheck: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id, disabled: !canDrag });
  return (
    <div ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition }} {...attributes} {...listeners}
      data-media-tile role="option" aria-selected={selected} aria-label={item.displayLabel ? `${item.displayLabel}, ${item.fileName}` : item.fileName}
      onClick={(e) => { e.stopPropagation(); onPointer(e); }} onDoubleClick={onOpen}
      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onOpen(); } else if (e.key === ' ') { e.preventDefault(); onCheck(); } }}
      className={`group relative select-none overflow-hidden rounded-xl border bg-white outline-none transition focus-visible:ring-2 focus-visible:ring-bronze-500/40 ${canDrag ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'} ${selected ? 'border-bronze-500 ring-2 ring-bronze-500/30' : 'border-ink-200 hover:border-ink-300'} ${isDragging ? 'opacity-40' : cut ? 'opacity-50' : ''}`}>
      <div className="relative aspect-[4/3] bg-ink-50">
        <MediaThumb item={item} />
        <button type="button" aria-label={selected ? 'Deselect' : 'Select'} tabIndex={-1}
          onClick={(e) => { e.stopPropagation(); onCheck(); }} onPointerDown={(e) => e.stopPropagation()}
          className={`absolute left-2 top-2 grid h-5 w-5 place-items-center rounded-md border shadow-sm transition ${selected ? 'border-bronze-500 bg-bronze-500 text-on-primary' : 'border-ink-300 bg-white/90 text-transparent opacity-0 group-hover:opacity-100'}`}>
          <Check className="h-3.5 w-3.5" strokeWidth={3} />
        </button>
        {cover && <span className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-full bg-white/95 px-2 py-0.5 text-[10.5px] font-semibold text-bronze-700 shadow-sm" title="The first photo is the cover on the website and the app"><Star className="h-3 w-3 fill-current" />Cover</span>}
        {item.displayLabel && <span className="absolute bottom-2 left-2 max-w-[calc(100%-1rem)] truncate rounded-md bg-white/95 px-1.5 py-0.5 text-[11px] font-semibold text-ink-900 shadow-sm">{item.displayLabel}</span>}
      </div>
      <div className="px-2.5 py-1.5">
        <p className="truncate text-[12px] text-ink-700" title={item.caption ?? item.fileName}>{item.caption || item.fileName}</p>
      </div>
    </div>
  );
}

function FolderTile({ f, onOpen, droppable }: { f: MediaFolder; onOpen: () => void; droppable: boolean }) {
  const { setNodeRef, isOver } = useDroppable({ id: `tile:${f.id}`, data: { folderId: f.id, name: f.name }, disabled: !droppable });
  const I = FOLDER_ICON[f.kind];
  return (
    <button ref={setNodeRef} type="button" onClick={(e) => { e.stopPropagation(); onOpen(); }}
      className={`flex items-center gap-2.5 rounded-xl border px-3 py-2.5 text-left transition ${isOver ? 'border-bronze-500 bg-bronze-50 ring-2 ring-bronze-500/30' : 'border-ink-200 bg-white hover:border-ink-300 hover:bg-ink-50'}`}>
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-ink-100 text-ink-500"><I className="h-4 w-4" /></span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[12.5px] font-medium text-ink-900">{f.name}</span>
        <span className="block text-[11px] text-ink-400">{f.isAuto ? 'Made automatically' : 'Folder'}{f.itemCount ? ` · ${plural(f.itemCount, 'file')}` : ''}</span>
      </span>
    </button>
  );
}

/** Pick a folder to move or paste into. */
function FolderPicker({ open, title, onClose, onPick, exclude }: { open: boolean; title: string; onClose: () => void; onPick: (f: MediaFolder) => void; exclude?: string }) {
  const tree = useQuery({ queryKey: ['media', 'tree'], queryFn: () => mediaApi.tree(), enabled: open });
  const [picked, setPicked] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const byId = useMemo(() => { const m = new Map<string, MediaFolder>(); const w = (f: MediaFolder) => { m.set(f.id, f); f.children?.forEach(w); }; (tree.data ?? []).forEach(w); return m; }, [tree.data]);
  const target = picked ? byId.get(picked) : undefined;
  return (
    <Sheet open={open} onClose={onClose} title={title} size="sm" subtitle="Files go to the end of the folder you choose."
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button disabled={!target || target.id === exclude} onClick={() => target && onPick(target)}>{target ? `Into ${target.name}` : 'Choose a folder'}</Button></>}>
      <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Find a building or folder" className="mb-3 w-full" />
      {tree.isLoading ? <div className="space-y-2">{Array.from({ length: 8 }, (_, i) => <Skeleton key={i} className="h-6 w-full" />)}</div>
        : <FolderTree roots={tree.data ?? []} selectedId={picked} onSelect={(id) => setPicked(id)} rootLabel={null} filter={filter} />}
    </Sheet>
  );
}

export function MediaGrid({ folder, subfolders = [], onOpenFolder, aside, header, toolbarEnd, emptyHint, onChanged }: {
  /** The open folder, or null for the top of the library (only sub-folders show there). */
  folder: GridFolder | null;
  subfolders?: MediaFolder[];
  onOpenFolder?: (id: string) => void;
  /** Rendered to the left inside the same drag context (the folder tree), so files can be dropped on its folders. */
  aside?: ReactNode;
  /** Above the toolbar (breadcrumbs, a Photos / Floorplans / EPC switch). */
  header?: ReactNode;
  /** More controls at the end of the toolbar (New folder, the folder's own menu). */
  toolbarEnd?: ReactNode;
  emptyHint?: string;
  /** Something changed that the caller may show elsewhere (the tree's counts). */
  onChanged?: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const can = { create: useCan('media.create'), update: useCan('media.update'), del: useCan('media.delete') };
  const writable = !!folder && !folder.archived;
  const folderId = folder?.id ?? null;
  const key = ['media', 'items', folderId] as const;
  const list = useQuery({ queryKey: key, queryFn: () => mediaApi.items(folderId!), enabled: !!folderId });
  const items = useMemo(() => list.data ?? [], [list.data]);
  const coverId = folder && COVER_FOLDERS.has(folder.kind) ? items.find((i) => i.kind === 'IMAGE')?.id : undefined;

  // ── Selection ──
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const anchor = useRef<string | null>(null);
  const [shownFolder, setShownFolder] = useState(folderId);
  if (shownFolder !== folderId) { setShownFolder(folderId); setSelected(new Set()); anchor.current = null; }
  const ids = useMemo(() => items.map((i) => i.id), [items]);
  const chosen = useMemo(() => items.filter((i) => selected.has(i.id)), [items, selected]);
  const pointer = (id: string, e: { shiftKey: boolean; metaKey: boolean; ctrlKey: boolean }) => {
    if (e.shiftKey && anchor.current && ids.includes(anchor.current)) {
      const [a, b] = [ids.indexOf(anchor.current), ids.indexOf(id)].sort((x, y) => x - y);
      const range = ids.slice(a, b + 1);
      setSelected((s) => (e.metaKey || e.ctrlKey ? new Set([...s, ...range]) : new Set(range)));
      return;
    }
    anchor.current = id;
    if (e.metaKey || e.ctrlKey) setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
    else setSelected(new Set([id]));
  };
  const toggle = (id: string) => { anchor.current = id; setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; }); };
  const selectAll = () => setSelected(new Set(ids));
  const clear = () => setSelected(new Set());

  // ── Refresh ──
  const refresh = useCallback((...folders: Array<string | null | undefined>) => {
    for (const f of new Set(folders)) if (f) void qc.invalidateQueries({ queryKey: ['media', 'items', f] });
    void qc.invalidateQueries({ queryKey: ['media', 'tree'] });
    void qc.invalidateQueries({ queryKey: ['media', 'folder'] });
    void qc.invalidateQueries({ queryKey: ['media', 'labels'] });
    onChanged?.();
  }, [qc, onChanged]);

  // ── Clipboard ──
  const clip = useMediaClipboard();
  const copy = (mode: 'copy' | 'cut') => {
    if (!chosen.length || !folderId) return;
    clip.set({ ids: chosen.map((i) => i.id), mode, fromFolderId: folderId });
    toast.ok(`${plural(chosen.length, 'file')} ${mode === 'copy' ? 'copied' : 'cut'} — open a folder and paste`);
  };
  const [busy, setBusy] = useState(false);
  const run = async (fn: () => Promise<string>) => {
    setBusy(true);
    try { toast.ok(await fn()); } catch (e) { toast.fail(e); }
    setBusy(false);
  };
  const pasteInto = (target: { id: string; name: string }) => run(async () => {
    const { ids: clipIds, mode, fromFolderId } = useMediaClipboard.getState();
    if (!clipIds.length) return 'Nothing to paste';
    if (mode === 'cut' && fromFolderId === target.id) { clip.clear(); return 'Those files are already here'; }
    const res = mode === 'copy' ? await mediaApi.copy(clipIds, target.id) : await mediaApi.move(clipIds, target.id);
    if (mode === 'cut') clip.clear();
    refresh(target.id, fromFolderId);
    if (target.id === folderId) setSelected(new Set(res.map((r) => r.id)));
    return `${plural(res.length, 'file')} ${mode === 'copy' ? 'copied' : 'moved'} to ${target.name}`;
  });
  const moveTo = (ids: string[], target: { id: string; name: string }) => run(async () => {
    if (target.id === folderId) return 'Those files are already here';
    const res = await mediaApi.move(ids, target.id);
    clear(); refresh(target.id, folderId);
    return `${plural(res.length, 'file')} moved to ${target.name}`;
  });

  // ── Delete / rename / download ──
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const remove = async () => {
    setDeleting(true);
    try {
      const n = chosen.length;
      await mediaApi.remove(chosen.map((i) => i.id));
      setConfirmDelete(false); clear(); refresh(folderId);
      toast.ok(`${plural(n, 'file')} deleted`);
    } catch (e) { toast.fail(e); }
    setDeleting(false);
  };
  const [renaming, setRenaming] = useState<MediaItem | null>(null);
  const [newName, setNewName] = useState('');
  const rename = () => run(async () => {
    if (!renaming) return '';
    await mediaApi.updateItem(renaming.id, { fileName: newName.trim() });
    setRenaming(null); refresh(folderId);
    return 'File renamed';
  });
  const download = () => run(async () => {
    if (chosen.length === 1) { await downloadItem(chosen[0]!); return 'Downloading'; }
    if (chosen.length > 1) { await downloadZip({ ids: chosen.map((i) => i.id) }); return `Downloading ${plural(chosen.length, 'file')} as a zip`; }
    if (folderId) { await downloadZip({ folderId }); return `Downloading ${folder?.name} as a zip`; }
    return '';
  });
  const [moving, setMoving] = useState(false);

  // ── Upload ──
  const picker = useRef<HTMLInputElement>(null);
  const [staged, setStaged] = useState<File[] | null>(null);
  const [dropping, setDropping] = useState(false);
  const canUpload = can.create && writable;
  const onFilesDragOver = (e: React.DragEvent) => {
    if (!canUpload || !Array.from(e.dataTransfer.types).includes('Files')) return;
    e.preventDefault(); e.dataTransfer.dropEffect = 'copy';
    if (!dropping) setDropping(true);
  };
  const onFilesDrop = (e: React.DragEvent) => {
    if (!canUpload) return;
    e.preventDefault(); setDropping(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length) setStaged(files);
  };

  // ── Preview ──
  const [preview, setPreview] = useState<number | null>(null);
  const closePreview = useCallback(() => setPreview(null), [setPreview]);

  // ── Drag to reorder / onto a folder ──
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const [dragging, setDragging] = useState<{ id: string; count: number } | null>(null);
  const onDragStart = (e: DragStartEvent) => {
    const id = String(e.active.id);
    let count = selected.size;
    if (!selected.has(id)) { setSelected(new Set([id])); anchor.current = id; count = 1; }
    setDragging({ id, count });
  };
  const onDragEnd = (e: DragEndEvent) => {
    const id = String(e.active.id);
    setDragging(null);
    if (!e.over || !folderId) return;
    const target = dropFolder(e.over.id);
    if (target) {
      const name = (e.over.data.current as { name?: string } | undefined)?.name;
      const moveIds = selected.has(id) ? [...selected] : [id];
      void moveTo(moveIds, { id: target, name: name ?? 'that folder' });
      return;
    }
    const overId = String(e.over.id);
    if (overId === id || !can.update || !writable) return;
    const from = ids.indexOf(id); const to = ids.indexOf(overId);
    if (from < 0 || to < 0) return;
    const order = arrayMove(ids, from, to);
    const before = list.data;
    qc.setQueryData<MediaItem[]>(key, order.map((x) => items.find((i) => i.id === x)!));
    mediaApi.reorder(folderId, order).then(
      (rows) => { qc.setQueryData(key, rows); onChanged?.(); },
      (err) => { qc.setQueryData(key, before); toast.fail(err); },
    );
  };

  // ── Keyboard ──
  const keys = useRef<(e: KeyboardEvent) => void>(() => undefined);
  const onKey = (e: KeyboardEvent) => {
    if (isTyping(e.target) || preview !== null || staged || confirmDelete || renaming || moving) return;
    if (document.querySelector('[role="dialog"]')) return;
    const mod = e.metaKey || e.ctrlKey;
    const k = e.key.toLowerCase();
    if (mod && k === 'a' && items.length) { e.preventDefault(); selectAll(); }
    else if (mod && k === 'c' && chosen.length) { e.preventDefault(); copy('copy'); }
    else if (mod && k === 'x' && chosen.length && can.update) { e.preventDefault(); copy('cut'); }
    else if (mod && k === 'v' && clip.ids.length && writable && (clip.mode === 'copy' ? can.create : can.update)) { e.preventDefault(); void pasteInto(folder!); }
    else if ((e.key === 'Delete' || e.key === 'Backspace') && chosen.length && can.del && writable) { e.preventDefault(); setConfirmDelete(true); }
    else if (e.key === 'Escape' && selected.size) clear();
  };
  useLayoutEffect(() => { keys.current = onKey; });
  useEffect(() => {
    const h = (e: KeyboardEvent) => keys.current(e);
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);

  const canPaste = writable && clip.ids.length > 0 && (clip.mode === 'copy' ? can.create : can.update);
  const dragged = dragging ? items.find((i) => i.id === dragging.id) : undefined;
  const nothing = !!folder && !list.isLoading && !list.isError && !items.length && !subfolders.length;

  return (
    <DndContext sensors={sensors} collisionDetection={collide} onDragStart={onDragStart} onDragEnd={onDragEnd} onDragCancel={() => setDragging(null)}>
      <div className="flex min-h-0 gap-4">
        {aside}
        <section className="min-w-0 flex-1" aria-label={folder ? `Files in ${folder.name}` : 'Media'}>
          {header}
          {/* Toolbar */}
          <div className="mb-3 flex flex-wrap items-center gap-1.5">
            {canUpload && <Button size="sm" icon="Upload" onClick={() => picker.current?.click()}>Upload</Button>}
            {toolbarEnd}
            <span className="mx-1 hidden h-5 w-px bg-ink-200 sm:block" aria-hidden />
            {folder && <Button size="sm" variant="secondary" icon="Download" disabled={busy || (!chosen.length && !items.length)} onClick={() => void download()}
              title={chosen.length ? undefined : 'The whole folder, as a zip'}>{chosen.length > 1 ? 'Download zip' : chosen.length ? 'Download' : 'Download all'}</Button>}
            {folder && <Button size="sm" variant="secondary" icon="Copy" disabled={!chosen.length} onClick={() => copy('copy')} title="⌘/Ctrl C">Copy</Button>}
            {folder && can.update && <Button size="sm" variant="secondary" icon="Scissors" disabled={!chosen.length} onClick={() => copy('cut')} title="⌘/Ctrl X — then paste in another folder">Cut</Button>}
            {canPaste && <Button size="sm" variant="secondary" icon="ClipboardPaste" disabled={busy} onClick={() => void pasteInto(folder!)} title="⌘/Ctrl V">Paste {clip.ids.length}</Button>}
            {folder && can.update && writable && <Button size="sm" variant="secondary" icon="FolderInput" disabled={!chosen.length || busy} onClick={() => setMoving(true)}>Move</Button>}
            {folder && can.update && writable && <Button size="sm" variant="secondary" icon="PencilLine" disabled={chosen.length !== 1} onClick={() => { setRenaming(chosen[0]!); setNewName(chosen[0]!.fileName); }}>Rename</Button>}
            {folder && can.del && writable && <Button size="sm" variant="secondary" icon="Trash2" disabled={!chosen.length} onClick={() => setConfirmDelete(true)} className="hover:!border-red-200 hover:!bg-red-50 hover:!text-red-700">Delete</Button>}
            <span className="ml-auto text-[12px] text-ink-500">
              {chosen.length
                ? <>{plural(chosen.length, 'file')} selected · <button type="button" className="font-medium text-bronze-600 hover:underline" onClick={clear}>Clear</button></>
                : folder && items.length ? <>{plural(items.length, 'file')} · <button type="button" className="font-medium text-bronze-600 hover:underline" onClick={selectAll}>Select all</button></> : null}
            </span>
          </div>
          {folder && can.update && writable && items.length > 1 && COVER_FOLDERS.has(folder.kind) && (
            <p className="-mt-1 mb-3 text-[11.5px] text-ink-400">Drag to change the order. The first photo is the cover on the website and the app.</p>
          )}

          {/* Grid (also the drop zone for files from the desktop) */}
          <div className="relative min-h-[240px] rounded-xl" onClick={clear}
            onDragOver={onFilesDragOver} onDragEnter={onFilesDragOver} onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setDropping(false); }} onDrop={onFilesDrop}>
            {subfolders.length > 0 && (
              <div className="mb-4 grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3">
                {subfolders.map((f) => <FolderTile key={f.id} f={f} droppable={can.update && writable} onOpen={() => onOpenFolder?.(f.id)} />)}
              </div>
            )}
            {folder && list.isLoading && (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5" role="status" aria-label="Loading">
                {Array.from({ length: 8 }, (_, i) => <div key={i} className="overflow-hidden rounded-xl border border-ink-200"><Skeleton className="aspect-[4/3] w-full rounded-none" /><div className="p-2"><Skeleton className="h-3 w-2/3" /></div></div>)}
              </div>
            )}
            {folder && list.isError && <EmptyState icon="AlertTriangle" title="These files could not be loaded" body={(list.error as Error)?.message} action={<Button size="sm" variant="secondary" onClick={() => void list.refetch()}>Try again</Button>} />}
            {nothing && (
              <EmptyState icon="Images" title={folder!.archived ? 'This folder is archived' : 'Nothing here yet'}
                body={folder!.archived ? 'Its building or flat was deleted. The files are kept.' : canUpload ? (emptyHint ?? 'Drag photos, PDFs or videos in from your computer, or use Upload.') : 'No files in this folder.'}
                action={canUpload ? <Button size="sm" icon="Upload" onClick={(e) => { e.stopPropagation(); picker.current?.click(); }}>Upload</Button> : undefined} />
            )}
            {!folder && !subfolders.length && <EmptyState icon="FolderOpen" title="No folders yet" body="Folders appear here for every building and flat." />}
            {items.length > 0 && (
              <SortableContext items={ids} strategy={rectSortingStrategy}>
                <div role="listbox" aria-multiselectable="true" aria-label="Files" className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5">
                  {items.map((it, i) => (
                    <Tile key={it.id} item={it} selected={selected.has(it.id)} cover={it.id === coverId}
                      cut={clip.mode === 'cut' && clip.ids.includes(it.id)} canDrag={can.update && writable}
                      onPointer={(e) => pointer(it.id, e)} onOpen={() => setPreview(i)} onCheck={() => toggle(it.id)} />
                  ))}
                </div>
              </SortableContext>
            )}
            {dropping && (
              <div className="pointer-events-none absolute inset-0 z-10 grid place-items-center rounded-xl border-2 border-dashed border-bronze-500 bg-bronze-50/85">
                <div className="flex flex-col items-center gap-2 text-bronze-700"><UploadCloud className="h-8 w-8" /><p className="text-[13.5px] font-semibold">Drop to add to {folder?.name}</p></div>
              </div>
            )}
          </div>
        </section>
      </div>

      <DragOverlay dropAnimation={null}>
        {dragged && (
          <div className="relative w-40 overflow-hidden rounded-xl border border-bronze-500 bg-white shadow-pop">
            <div className="aspect-[4/3]"><MediaThumb item={dragged} /></div>
            {dragging!.count > 1 && <span className="absolute -right-0 top-1 mr-1 rounded-full bg-bronze-500 px-2 py-0.5 text-[11px] font-semibold text-on-primary">{dragging!.count}</span>}
          </div>
        )}
      </DragOverlay>

      <input ref={picker} type="file" multiple accept={MEDIA_ACCEPT} className="hidden"
        onChange={(e) => { const files = Array.from(e.target.files ?? []); e.target.value = ''; if (files.length) setStaged(files); }} />
      {folder && (
        <UploadSheet open={!!staged} folder={folder} files={staged ?? []} onClose={() => setStaged(null)}
          onUploaded={(saved, failed) => { refresh(folder.id); if (saved.length) setSelected(new Set(saved.map((s) => s.id))); if (saved.length && !failed) toast.ok(`${plural(saved.length, 'file')} uploaded to ${folder.name}`); else if (failed) toast.fail(new Error(`${plural(failed, 'file')} could not be uploaded`)); }} />
      )}
      <MediaPreview items={items} index={preview} onIndex={setPreview} onClose={closePreview} canUpdate={can.update && writable}
        onSaved={(row) => { qc.setQueryData<MediaItem[]>(key, (old) => old?.map((i) => (i.id === row.id ? row : i))); refresh(folderId); }} />
      <Confirm open={confirmDelete} onClose={() => setConfirmDelete(false)} pending={deleting} confirmLabel={`Delete ${plural(chosen.length, 'file')}`}
        title={`Delete ${plural(chosen.length, 'file')}?`} onConfirm={() => void remove()}
        body={<>{chosen.length === 1 ? <b>{chosen[0]!.displayLabel ?? chosen[0]!.fileName}</b> : 'These files'} will be removed from {folder?.name}{folder && COVER_FOLDERS.has(folder.kind) ? ' and from the website and the app' : ''}. Copies in other folders are kept.</>} />
      <Sheet open={!!renaming} onClose={() => setRenaming(null)} size="sm" title="Rename file"
        footer={<><Button variant="secondary" onClick={() => setRenaming(null)}>Cancel</Button><Button disabled={!newName.trim() || newName.trim() === renaming?.fileName || busy} onClick={() => void rename()}>Rename</Button></>}>
        <Input autoFocus value={newName} maxLength={200} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && newName.trim()) void rename(); }} className="w-full" />
      </Sheet>
      <FolderPicker open={moving} title={`Move ${plural(chosen.length, 'file')}`} exclude={folderId ?? undefined} onClose={() => setMoving(false)}
        onPick={(t) => { setMoving(false); void moveTo(chosen.map((i) => i.id), t); }} />
      {toast.node}
    </DndContext>
  );
}
